/**
 * TanStack Router root — admin SPA shell.
 *
 * Routes (issue #10):
 *   /            — redirects to /sites if authed, /login otherwise.
 *   /login       — login form (public).
 *   /signup      — signup form (public).
 *   /sites       — list of sites (auth-required).
 *   /sites/:id   — site detail with Pages | Domains tabs (auth-required).
 *   *            — 404 not-found page.
 *
 * Authenticated routes are wrapped in `<RequireAuth>` which calls
 * `useMe()` and redirects to /login on a 401. We intentionally use a
 * component-level guard rather than `beforeLoad` because the latter
 * runs outside of React and cannot easily share a TanStack Query
 * `QueryClient` for caching the `me` lookup.
 */

import type { ReactElement } from "react";
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  RouterProvider,
  redirect,
} from "@tanstack/react-router";
import { Box } from "@mui/material";
import { RequireAuth } from "./components/RequireAuth";
import { AppLayout } from "./components/AppLayout";
import { LoginPage } from "./pages/LoginPage";
import { SignupPage } from "./pages/SignupPage";
import { SitesListPage } from "./pages/SitesListPage";
import { SiteDetailPage } from "./pages/SiteDetailPage";
import { PageDetailPage } from "./pages/PageDetailPage";
import { ContentItemsListPage } from "./pages/ContentItemsListPage";
import { ContentItemDetailPage } from "./pages/ContentItemDetailPage";
import { AiStudioPage } from "./pages/AiStudioPage";
import { AiUsagePage } from "./pages/AiUsagePage";
import { LeadsPage } from "./pages/LeadsPage";
import { AnalyticsPage } from "./pages/AnalyticsPage";
import { ExperimentsPage } from "./pages/ExperimentsPage";
import { ExperimentDetailPage } from "./pages/ExperimentDetailPage";
import { EnvDiagnosticsPage } from "./pages/EnvDiagnosticsPage";
import { OrganizationDetailPage } from "./pages/OrganizationDetailPage";
import { NotFoundPage } from "./pages/NotFoundPage";

const rootRoute = createRootRoute({
  component: () => (
    <Box>
      <Outlet />
    </Box>
  ),
  notFoundComponent: () => <NotFoundPage />,
});

/**
 * `/` — bounce. We can't synchronously know whether the operator is signed
 * in (the cookie is HttpOnly), so we redirect to /sites and let the auth
 * guard there fall through to /login if the session is missing.
 */
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  beforeLoad: () => {
    // TanStack Router uses `throw redirect(...)` as its idiomatic
    // navigation primitive in `beforeLoad` — the thrown value is a
    // sentinel object the router intercepts, not an Error. The
    // eslint-only-throw-error rule doesn't know this, hence the
    // localised disable.
    // eslint-disable-next-line @typescript-eslint/only-throw-error
    throw redirect({ to: "/sites" });
  },
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage,
});

const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/signup",
  component: SignupPage,
  /**
   * Issue #276 — multi-tenancy Phase 5.  The signup page accepts an
   * optional `?invite=<token>` carrying an OrganizationInvitation
   * token.  Validated as a bounded string here so the router's typed
   * search exposes it cleanly to `useSearch({ from: "/signup" })`.
   * Invalid shapes (e.g. an array) fall through to `undefined`.
   */
  validateSearch: (search: Record<string, unknown>): { invite?: string } => {
    const raw = search.invite;
    if (typeof raw === "string" && raw.trim().length > 0 && raw.length <= 200) {
      return { invite: raw.trim() };
    }
    return {};
  },
});

/**
 * Authenticated layout pathless route. Children render inside the
 * `<AppLayout>` shell (top bar, sidebar, content) which itself sits inside
 * `<RequireAuth>`. TanStack Router's "id" + no-`path` form layers the
 * shell without consuming a URL segment.
 */
const authenticatedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "authenticated",
  component: () => (
    <RequireAuth>
      <AppLayout />
    </RequireAuth>
  ),
});

const sitesIndexRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/sites",
  component: SitesListPage,
});

/**
 * Site detail carries a typed `?tab=` search param so a refresh / shared
 * link lands the operator back on the tab they were viewing. Invalid
 * values fall back to the SiteDetailPage's own default (`pages`); the
 * URL is operator-facing and a fat-fingered typo should never crash the
 * page.
 */
const SITE_DETAIL_TABS = ["pages", "domains", "theme", "content"] as const;
type SiteDetailTab = (typeof SITE_DETAIL_TABS)[number];

const siteDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/sites/$siteId",
  component: SiteDetailPage,
  validateSearch: (search: Record<string, unknown>): { tab?: SiteDetailTab } => {
    const raw = search.tab;
    if (typeof raw === "string" && (SITE_DETAIL_TABS as readonly string[]).includes(raw)) {
      return { tab: raw as SiteDetailTab };
    }
    return {};
  },
});

/**
 * `/sites/$siteId/pages/$pageId` — PageVersion lifecycle editor (#172).
 * Lands the operator on the JSON editor + publish / unpublish / rollback
 * surface for a single page. Mounted beneath the same `authenticated`
 * pathless layout as the rest of the admin SPA.
 */
const pageDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/sites/$siteId/pages/$pageId",
  component: PageDetailPage,
});

/**
 * `/sites/$siteId/content` and `/sites/$siteId/content/$contentItemId` —
 * standalone ContentItem admin (#173). The list page deep-links from the
 * SiteDetailPage Content tab; the detail page exposes the per-version
 * editor and the per-version preview affordance.
 */
const contentItemsListRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/sites/$siteId/content",
  component: ContentItemsListPage,
});

const contentItemDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/sites/$siteId/content/$contentItemId",
  component: ContentItemDetailPage,
});

/**
 * `/ai/studio` and `/ai/usage` — AI admin surface (#19). Both live
 * beneath the pathless `authenticated` layout route so the operator
 * gets the standard shell + sidebar.
 */
const aiStudioRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/ai/studio",
  component: AiStudioPage,
});

const aiUsageRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/ai/usage",
  component: AiUsagePage,
});

/**
 * `/leads` — Lead admin (#21). Lives under the same authenticated layout
 * as the rest of the admin SPA so the operator gets the standard shell +
 * sidebar.
 */
const leadsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/leads",
  component: LeadsPage,
});

/**
 * `/analytics` — admin Analytics dashboard (#24). KPI tiles + daily chart
 * + top-pages / bot-vs-human / AI-scraper / referrer breakdowns. Same
 * authenticated layout as the rest of the admin SPA.
 */
const analyticsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/analytics",
  component: AnalyticsPage,
});

/**
 * `/experiments` and `/experiments/$experimentId` — Experiment + Variant
 * CRUD (#25). Same authenticated layout as the rest of the admin SPA.
 * The detail route consumes a `$experimentId` param via TanStack
 * Router's typed-params API.
 */
const experimentsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/experiments",
  component: ExperimentsPage,
});

const experimentDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/experiments/$experimentId",
  component: ExperimentDetailPage,
});

/**
 * `/admin/env` — env diagnostics admin page (#266). Lists every required
 * platform env var with status (ok / missing / placeholder /
 * using-default), masks secret values, and exposes audit-logged reveal +
 * copy affordances. Mounted under the same authenticated layout as the
 * rest of the admin SPA.
 */
const envDiagnosticsRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/admin/env",
  component: EnvDiagnosticsPage,
});

/**
 * `/admin/organizations/$orgId` — Org admin detail page (#275). Members
 * table + rename form, scoped to the org id in the URL. The OrgSwitcher
 * in the top bar deep-links here when the operator picks "Organization
 * settings" from the menu. TanStack Router's typed `$orgId` param is
 * consumed in the page via `useParams({ from: "/authenticated/..." })`.
 */
const organizationDetailRoute = createRoute({
  getParentRoute: () => authenticatedRoute,
  path: "/admin/organizations/$orgId",
  component: OrganizationDetailPage,
});

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  signupRoute,
  authenticatedRoute.addChildren([
    sitesIndexRoute,
    siteDetailRoute,
    pageDetailRoute,
    contentItemsListRoute,
    contentItemDetailRoute,
    aiStudioRoute,
    aiUsageRoute,
    leadsRoute,
    analyticsRoute,
    experimentsRoute,
    experimentDetailRoute,
    envDiagnosticsRoute,
    organizationDetailRoute,
  ]),
]);

export const router = createRouter({ routeTree, defaultNotFoundComponent: NotFoundPage });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

export function AppRouter(): ReactElement {
  return <RouterProvider router={router} />;
}
